import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { SPOTLIGHTS, spotlightText } from './Spotlights';
import { categoryLabel, einzelplanLabel, einzelplanName, lookupLabel, t, useT, type LookupFamily } from '../i18n';
import type { FilterOptions, FilterState, HierarchicalFilterOption, SelectedTitle } from '../types';

// Every slot that gets a chip. Beyond the FilterState keys this includes the
// chart drill position (`drillDownType`/`drillDownSubType`/`selectedTitle`),
// which lives OUTSIDE FilterState — a chart drill deliberately clears
// filters.expenditureType (see the DRILL_DOWN reducer) even though it scopes
// the charts and the table just as hard as a panel filter does.
// `type` (Einnahmen/Ausgaben) stays out: it is a mode, not a narrowing
// filter, and the toggle above the search bar already makes it visible.
export type ChipKey =
  | 'query'
  | 'expenditureType'
  | 'subExpType'
  | 'einzelplan'
  | 'kapitel'
  | 'hauptgruppe'
  | 'obergruppe'
  | 'gruppierung'
  | 'hauptfunktion'
  | 'oberfunktion'
  | 'funktion'
  | 'prefix'
  | 'kapitelScope'
  | 'drillDownType'
  | 'drillDownSubType'
  | 'selectedTitle';

interface Props {
  filters: FilterState;
  filterOptions: FilterOptions | null;
  /** Chart drill position — see the ChipKey comment on why it's separate. */
  drillDownType: string | null;
  drillDownSubType: string | null;
  selectedTitle: SelectedTitle | null;
  onRemove: (key: ChipKey) => void;
  /** Wipes everything — every panel filter, the search term, the drill
      position, spotlights. Surfaced as a red "Alles zurücksetzen" pill at the
      start of the bar whenever anything is active. */
  onClearAll: () => void;
}

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-family: var(--sans-stack);
`;

const Caption = styled.span`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
  margin-right: ${theme.spacing.xs};
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px 3px 10px;
  border-radius: 999px;
  background: var(--color-data-100);
  border: 1px solid color-mix(in srgb, var(--color-data) 40%, transparent);
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.primary};
  white-space: nowrap;
`;

const ChipKeyLabel = styled.span`
  color: ${theme.colors.text.muted};
`;

// Titel labels are full Zweckbestimmung sentences, which would stretch a chip
// past the whole bar. Cap the value and ellipse it; short values are
// unaffected, so every other chip keeps its natural width.
const ChipValue = styled.span`
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  &:hover { background: color-mix(in srgb, var(--color-data) 35%, transparent); color: ${theme.colors.text.primary}; }
`;

// "Reset everything" pill at the start of the bar. Mirrors the chip shape
// but swaps the data-tint for the error red — the chip is destructive
// (wipes every filter dimension and the drill position at once) so it should
// not blend in with the per-chip × buttons.
const ResetAll = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: color-mix(in srgb, ${theme.colors.error} 10%, transparent);
  border: 1px solid color-mix(in srgb, ${theme.colors.error} 45%, transparent);
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.colors.error};
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: color-mix(in srgb, ${theme.colors.error} 22%, transparent);
    border-color: ${theme.colors.error};
  }
`;

// Filter slots rendered after the Politikfeld/drill block, in sidebar order.
const TAIL_KEYS = [
  'einzelplan',
  'kapitel',
  'kapitelScope',
  'hauptgruppe',
  'obergruppe',
  'gruppierung',
  'hauptfunktion',
  'oberfunktion',
  'funktion',
  'prefix',
] as const satisfies readonly ChipKey[];

function findInTree(
  tree: HierarchicalFilterOption[] | undefined,
  value: string,
): HierarchicalFilterOption | null {
  if (!tree) return null;
  for (const node of tree) {
    if (node.value === value) return node;
    const found = node.children ? findInTree(node.children, value) : null;
    if (found) return found;
  }
  return null;
}

// The tree a slot's values live in, plus the flat option list to fall back on
// when the server response predates the trees.
function labelSources(key: ChipKey, filterOptions: FilterOptions) {
  switch (key) {
    case 'expenditureType':
    case 'drillDownType':
      return { tree: filterOptions.politikfeldTree, flat: filterOptions.expenditureTypes };
    case 'subExpType':
    case 'drillDownSubType':
      return { tree: filterOptions.politikfeldTree, flat: filterOptions.subExpenditureTypes };
    case 'einzelplan':
      return { tree: filterOptions.haushaltsstelleTree, flat: filterOptions.einzelplan };
    case 'kapitel':
      return { tree: filterOptions.haushaltsstelleTree, flat: filterOptions.kapitel };
    case 'hauptgruppe':
      return { tree: filterOptions.gruppierungTree, flat: filterOptions.hauptgruppe };
    case 'obergruppe':
      return { tree: filterOptions.gruppierungTree, flat: filterOptions.obergruppe };
    case 'gruppierung':
      return { tree: filterOptions.gruppierungTree, flat: filterOptions.gruppierung };
    case 'hauptfunktion':
      return { tree: filterOptions.funktionTree, flat: filterOptions.hauptfunktion };
    case 'oberfunktion':
      return { tree: filterOptions.funktionTree, flat: filterOptions.oberfunktion };
    case 'funktion':
      return { tree: filterOptions.funktionTree, flat: filterOptions.funktion };
    default:
      return null;
  }
}

/**
 * Human-readable chip text for a filter value. Falls back through: the
 * dimension's tree → its flat option list → a matching spotlight preset
 * (covers values no option carries, like the Investitionen preset's
 * hauptgruppe "7,8" or the "B" prefix) → the raw value with its compound
 * "parent|child" prefix stripped.
 *
 * `lookupValues` lets a caller try alternative encodings first: a drilled
 * sub-type is a bare name ("Bildung") while the tree keys it compound
 * ("Subventionen an Unternehmen|Bildung").
 */
/** Kapitel / Gruppierung / Funktion labels in the widget language. */
function lookupDisplay(key: ChipKey, label: string): string {
  const family: LookupFamily | null = key === 'kapitel' ? 'kapitel'
    : key === 'hauptgruppe' || key === 'obergruppe' || key === 'gruppierung' ? 'gruppierung'
    : key === 'hauptfunktion' || key === 'oberfunktion' || key === 'funktion' ? 'funktion'
    : null;
  return family ? lookupLabel(family, label) : label;
}

function displayValue(
  key: ChipKey,
  value: string,
  filterOptions: FilterOptions | null,
  lookupValues: string[] = [value],
): string {
  if (key === 'query') return t().chips.query(value);

  // Politikfeld, Einzelplan, Kapitel, Gruppierung and Funktion labels are data
  // values with an English display name.
  const isCategory = key === 'expenditureType' || key === 'subExpType'
    || key === 'drillDownType' || key === 'drillDownSubType';

  if (filterOptions) {
    const sources = labelSources(key, filterOptions);
    if (sources) {
      for (const candidate of lookupValues) {
        const node = findInTree(sources.tree, candidate);
        if (node) {
          if (isCategory) return categoryLabel(node.label);
          return key === 'einzelplan' ? einzelplanLabel(node.label) : lookupDisplay(key, node.label);
        }
        const option = sources.flat?.find(o => o.value === candidate);
        if (option) {
          if (isCategory) return categoryLabel(option.label);
          return key === 'einzelplan' ? einzelplanName(option.label) : lookupDisplay(key, option.label);
        }
      }
    }
  }

  const spotlight = SPOTLIGHTS.find(s => s.filterType === key && s.filterValue === value);
  if (spotlight) return spotlightText(spotlight).label;

  // Compound values are "<parent>|<child>" (e.g. kapitel "60|92"); only the
  // child half is meaningful next to the dimension's own key label.
  const raw = value.includes('|') ? value.split('|')[1] || value : value;
  return isCategory ? categoryLabel(raw) : raw;
}

export function ActiveFilterChips({
  filters,
  filterOptions,
  drillDownType,
  drillDownSubType,
  selectedTitle,
  onRemove,
  onClearAll,
}: Props) {
  const m = useT();
  // Key labels (i18n chips.keys) match the SearchableSelect labels in
  // FilterPanel, so a chip reads the same as the control that produced it.
  // The drill keys borrow the Politikfeld labels — a drill and a panel pick
  // narrow the same two levels.
  const KEY_LABELS: Record<ChipKey, string> = m.chips.keys;
  const chips: Array<{ key: ChipKey; label: string }> = [];
  const add = (key: ChipKey, value: string, lookupValues?: string[]) =>
    chips.push({ key, label: displayValue(key, value, filterOptions, lookupValues) });

  if (filters.query) add('query', filters.query);

  // Politikfeld level 1. A panel pick sets the filter slot *and* drills, so
  // prefer the filter (its × restores the unfiltered view); a chart drill sets
  // only the drill slot. Both can never disagree — when the panel sets both,
  // it sets them to the same value.
  if (filters.expenditureType) {
    add('expenditureType', filters.expenditureType);
  } else if (drillDownType) {
    add('drillDownType', drillDownType);
  }

  // Politikfeld level 2. drillDownSubType is '' for a category that was
  // auto-skipped because it has a single sub-type (and for Einnahmen, which
  // have none) — there is no sub-type to name, so no chip.
  if (filters.subExpType) {
    add('subExpType', filters.subExpType);
  } else if (drillDownSubType) {
    add('drillDownSubType', drillDownSubType, [
      drillDownType ? `${drillDownType}|${drillDownSubType}` : drillDownSubType,
      drillDownSubType,
    ]);
  }

  // Level 3: one specific Titel, picked in a chart or the table.
  if (selectedTitle) add('selectedTitle', selectedTitle.zweckbestimmung);

  for (const key of TAIL_KEYS) {
    const value = filters[key];
    if (value) add(key, value);
  }

  if (chips.length === 0) return null;

  return (
    <Bar>
      <ResetAll onClick={onClearAll} type="button" aria-label={m.chips.resetAllAria}>
        {m.chips.resetAll}
      </ResetAll>
      <Caption>{m.chips.caption}</Caption>
      {chips.map(({ key, label }) => (
        <Chip key={key}>
          <ChipKeyLabel>{KEY_LABELS[key]}</ChipKeyLabel>
          <ChipValue title={label}>{label}</ChipValue>
          <RemoveBtn
            onClick={() => onRemove(key)}
            aria-label={m.chips.removeAria(KEY_LABELS[key])}
            title={m.common.removeFilter}
          >
            ×
          </RemoveBtn>
        </Chip>
      ))}
    </Bar>
  );
}
